import React, { useContext, useState, useCallback } from "react";

import { makeDummyRequest } from "api/makeApiRequest/makeDummyRequest";
import { AlertContext } from "components/AlertProvider/AlertProvider";
import MeetingSummary from "components/MeetingSummary";
import FieldCheckbox from "components/FieldCheckbox";
import { OutputRow as MeetingsDataFlattened } from "api/getMeetingsFlattened";
import { OutputRow as MeetingRequestsData } from "api/getMeetingRequests";
import { AuthContext } from "components/AuthProvider/AuthProvider";
import { useSendData } from "hooks/useSendData";
import { cancelMeeting } from "api/cancelMeeting";
import { cancelMeetingRequest } from "api/cancelMeetingRequest";
import terminology from "terminology.json";
import ModalCancelPurchase from "components/PageDashboard/ModalCancelPurchase";

import { isMeetingRequest } from "./isMeetingRequest";

export interface Props {
	event: MeetingsDataFlattened | MeetingRequestsData | undefined;
	isOpen: boolean;
	onCancel: () => Promise<void>;
	onOK: (arg: { requestedRebook: boolean; hostUid: string }) => Promise<void>;
}

const ModalCancelMeeting: React.FunctionComponent<Props> = ({
	event,
	isOpen,
	onCancel,
	onOK: onOKSupplied
}) => {
	const [{ uid }] = useContext(AuthContext);
	const [optInRebook, setOptInRebook] = useState<boolean | undefined>();

	const doCancelMeeting = useSendData({ method: cancelMeeting });
	const doCancelMeetingRequest = useSendData({ method: cancelMeetingRequest });
	const [addAlert] = useContext(AlertContext);
	const cancelItem = useCallback(
		({ item, message }, auth) => {
			const { id, hostUid } = item;

			if (uid !== hostUid && optInRebook === undefined) {
				addAlert({
					contents: "Please indicate if you want to book a replacement meeting"
				});
				return makeDummyRequest({}, "cancel-meeting");
			}

			const theitemIsAMeetingRequest = event && isMeetingRequest(item);

			const param = {
				id,
				...(message ? { message } : {})
			};

			if (theitemIsAMeetingRequest) {
				return doCancelMeetingRequest(param, auth);
			} else {
				return doCancelMeeting(param, auth);
			}
		},
		[doCancelMeeting, doCancelMeetingRequest, uid, optInRebook, addAlert, event]
	);

	const onOK = useCallback(
		async arg => {
			await onOKSupplied({
				...arg,
				requestedRebook: !!optInRebook
			});
			setOptInRebook(undefined);
		},
		[optInRebook, onOKSupplied]
	);

	return (
		<ModalCancelPurchase
			item={event}
			isOpen={!!event}
			cancelItem={cancelItem}
			onCancel={onCancel}
			onOK={onOK}
			itemDescription={terminology.meeting}
			summary={(item, currentUid) => (
				<MeetingSummary event={item} currentUid={currentUid} />
			)}
			heading={(item, currentUid) =>
				uid === item.hostUid
					? `Cancel ${terminology.meeting}`
					: `What in particular do you want to do?`
			}
			extraFormContent={(item, currentUid) =>
				uid === item.hostUid ? (
					<>
						<p>
							If you wish to add an accompanying note, please type it below.
						</p>
					</>
				) : (
					<>
						<div className="col-6 col-12-small">
							<FieldCheckbox
								id="response-decline"
								name="response"
								checked={optInRebook === false}
								onChange={e => {
									setOptInRebook(e.currentTarget.checked ? false : optInRebook);
								}}
								labelText="Cancel the booking"
							/>
						</div>

						<div className="col-6 col-12-small">
							<FieldCheckbox
								id="response-accept"
								name="response"
								checked={optInRebook === true}
								onChange={e => {
									setOptInRebook(e.currentTarget.checked ? true : optInRebook);
								}}
								labelText="Cancel and book a replacement"
							/>
						</div>
					</>
				)
			}
		/>
	);
};

export default ModalCancelMeeting;
